/**
 * Cookie-session identity for the BFF (Backend-for-Frontend) auth flow.
 *
 * The browser never holds a token in JS. Identity rides the HttpOnly
 * `egpt_session` cookie, set by the backend's /auth/callback. Every request
 * goes out with `credentials: "include"` and no Authorization header.
 *
 * On a 401 (session expired), the SPA redirects to /auth/login which triggers
 * a new OIDC authorize against BetterAuth. Since BetterAuth's server-side
 * session is long-lived, the authorize endpoint immediately issues a new code
 * without showing the login page (silent re-auth). The user sees a brief
 * redirect and lands back in the app with a fresh session cookie.
 *
 * This module deliberately exposes NO token getter/setter — there is nothing
 * for the SPA to store. Callers needing an authed fetch use `apiFetch`.
 */

/** Bounce to the BFF login (full navigation, not an SPA route) so BetterAuth's
 *  Authorization-Code + PKCE dance runs server-side. Preserves the would-be
 *  destination so /auth/callback can route the user back. Idempotent: a burst
 *  of 401s on one page won't loop. */
/** The embeddable chat (/embed.html) authenticates with a backend-mint userJWT
 *  passed in the URL fragment — it has NO OIDC cookie session, so a 401 on a
 *  cookie-authed workspace call (e.g. useCompany -> /v1/companies/me) must NOT
 *  bounce to the IdP. The IdP refuses to be framed (frame-ancestors), which
 *  would blank the iframe. In embed context the redirect is a no-op; the
 *  failing call is returned to its caller (which already fails silent). */
function _isEmbedContext(): boolean {
  try {
    return window.location.pathname.endsWith("/embed.html");
  } catch {
    return false;
  }
}

let _redirecting = false;
export function redirectToLogin(apiBase = ""): void {
  if (_redirecting || _isEmbedContext()) return;
  _redirecting = true;
  const from = window.location.pathname + window.location.search;
  // Full navigation drops in-flight React state and hands off to the backend.
  window.location.assign(`${apiBase}/auth/login?from=${encodeURIComponent(from)}`);
}

/** Test-only: clear the once-per-page redirect guard so each test starts fresh.
 *  Not used in production (a real navigation tears the page down anyway). */
export function __resetRedirectGuardForTests(): void {
  _redirecting = false;
}

/** Authed fetch over the cookie session.
 *   - always sends `credentials: "include"` (no Authorization header)
 *   - on 401 (session expired), redirects to /auth/login for silent re-auth
 *     via BetterAuth's still-alive server session, then returns the failing
 *     response so callers don't crash on `.json()`.
 *   - a 403 is an AUTHORIZATION failure (authenticated, but lacks the role for
 *     this action — e.g. a non-admin trying to install a plugin). It is NOT a
 *     session problem: it is returned to the caller to surface as an error.
 *
 *  Use for any call that requires the user's session. Callers must NOT add an
 *  Authorization header — the cookie is the single source of truth.
 */
export interface ApiFetchOptions {
  /** Default true. Pass false for background fetches (e.g. a cited image)
   *  whose 401 must not navigate the page away mid-conversation; the 401 is
   *  returned to the caller, which fails silently. */
  redirectOnUnauthorized?: boolean;
}

export async function apiFetch(
  input: RequestInfo | URL,
  init: RequestInit = {},
  { redirectOnUnauthorized = true }: ApiFetchOptions = {},
): Promise<Response> {
  const withCreds: RequestInit = { ...init, credentials: "include" };
  const resp = await fetch(input, withCreds);
  if (resp.status === 401 && redirectOnUnauthorized) {
    redirectToLogin();
  }
  return resp;
}
